export type TaskKind = "poster" | "video";

export interface TaskBrief {
  id: number;
  status: string;
  createdAt: string;
  /** 作品名(`/poster/tasks`、`/video/tasks` 列表本来就回 `name`;1004 T11 起上屏,老数据可能缺)。 */
  name?: string | null;
}

/**
 * 「最近作品」一行的显示名(1004 T11):作品名优先;没名字才退回「海报 #id」。
 * ★此前整行只有「#123 海报」—— 任务号对用户不是认得出的东西,五条作品看起来一模一样。
 */
export function creationTitle(row: TaskRow, kindLabel: string): string {
  const name = (row.name ?? "").replace(/\s+/g, " ").trim();
  return name || `${kindLabel} #${row.id}`;
}
export interface TaskRow extends TaskBrief {
  kind: TaskKind;
}

const TERMINAL = new Set(["SUCCESS", "FAILED", "TIMEOUT", "REMOVED", "BLOCKED"]);

export function isTerminal(status?: string): boolean {
  return status != null && TERMINAL.has(status);
}

/** 列表里只要有一条非终态就继续 5s 轮询,否则停。 */
export function nextRefetchInterval(items: { status: string }[]): number | false {
  return items.some((i) => !isTerminal(i.status)) ? 5000 : false;
}

/** 合并 poster/video 两源,打 kind 标,按 createdAt 倒序取最近 limit 条。 */
export function mergeRecent(posters: TaskBrief[], videos: TaskBrief[], limit: number): TaskRow[] {
  const tagged: TaskRow[] = [
    ...posters.map((t) => ({ ...t, kind: "poster" as const })),
    ...videos.map((t) => ({ ...t, kind: "video" as const })),
  ];
  return tagged
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
    .slice(0, limit);
}

/**
 * 在途任务的「已等待多久」(0830 P1-6 B 层)。
 *
 * <p>三位测试同学报的是同一件事:提交之后**看不出它还在不在动**。莉莉原话
 * 「调用 10 分钟工具了还没有结果,**卡住不动了**」——「卡住」是个**推断**,她其实无从判断:
 * 屏幕上那一行从头到尾只有「处理中」三个字,**没有任何随时间变化的量**。
 * 一个会往上走的数字,就能把"卡住了"变成"还在跑,已经 10 分钟了"。
 *
 * <p>★数据是现成的:列表口本来就返回 `createdAt`,**零后端成本**(分段进度才需要后端透出,那是 C 层)。
 * <p>★`now` 由调用方注入,不在函数里读 `Date.now()` —— 否则这个纯函数就没法被稳定断言。
 *
 * @returns 可直接上屏的一句话;**取不到或取坏了一律 `null`**(调用方据此不渲染)。
 *          宁可不显示,也不能在用户干等的时候给他一个「已等待 NaN 分钟」。
 */
export function waitedLabel(createdAt: string | null | undefined, now: number): string | null {
  if (!createdAt) return null;
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return null;
  // 客户端时钟比服务端快时 diff 会是负数 —— 按「刚提交」处理,不让负号上屏。
  const min = Math.floor(Math.max(0, now - t) / 60000);
  if (min < 1) return "刚提交";
  if (min < 60) return `已等待 ${min} 分钟`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `已等待 ${h} 小时` : `已等待 ${h} 小时 ${m} 分钟`;
}
